// A move is a route, not a destination (slice 08; design/12 §4): the squares
// the token passed through, produced by the player's own input. Snapping on,
// a drag records the centre of each cell the hand crosses; snapping off, it
// samples the hand's path every part of a cell. The route commits on release
// as one event, and everyone — the mover included — watches the token
// travel it. Pure; the board calls these with the grid it draws.
import { cellOf, snapToCells, type Grid, type Point, type Size } from '@dragons-table/vtt-shared';

export interface Extent { width: number; height: number }

/** How far the hand moves, snapping off, before another point is recorded: a fraction of a cell. */
const FREE_SAMPLE = 0.5;

const same = (a: Point, b: Point) => Math.abs(a.x - b.x) < 1e-6 && Math.abs(a.y - b.y) < 1e-6;

/**
 * Extend a route with where the hand is now. Snapping on: the block centre
 * for the cell under the hand, added when it is a new cell. Snapping off:
 * the hand's point, added when it has moved half a cell since the last.
 * The first point of a route is where the token started.
 */
export function extendRoute(route: Point[], hand: Point, grid: Grid, extent: Extent, size: Size, snap: boolean): Point[] {
  if (snap) {
    const { centre } = snapToCells(grid, extent, hand, size);
    const last = route[route.length - 1];
    if (last && same(last, centre)) return route;
    if (last) {
      const a = cellOf(grid, extent, last); const b = cellOf(grid, extent, centre);
      if (a.i === b.i && a.j === b.j) return route;
    }
    return [...route, centre];
  }
  const last = route[route.length - 1];
  if (!last) return [hand];
  const pitch = grid.x.basePitch * FREE_SAMPLE;
  if (Math.hypot(hand.x - last.x, hand.y - last.y) < pitch) return route;
  return [...route, { x: hand.x, y: hand.y }];
}

/** The route's end, snapped or free, as the move will commit it. */
export function finishRoute(route: Point[], hand: Point, grid: Grid, extent: Extent, size: Size, snap: boolean): Point[] {
  const end = snap ? snapToCells(grid, extent, hand, size).centre : { x: hand.x, y: hand.y };
  const last = route[route.length - 1];
  if (last && same(last, end)) return route;
  return [...route, end];
}

/**
 * A route no longer than the wire accepts: the ends kept, the points between
 * thinned evenly. A long free drag across a big map samples more points than
 * the limit, and the server would refuse the whole move for it.
 */
export function capRoute(route: Point[], max: number): Point[] {
  if (route.length <= max || max < 2) return route;
  const out: Point[] = [route[0]!];
  const inner = max - 2;
  for (let k = 1; k <= inner; k += 1) out.push(route[Math.round((k * (route.length - 1)) / (inner + 1))]!);
  out.push(route[route.length - 1]!);
  return out;
}

/** The point along a route at `t` in [0, 1], for the animation, at a constant pace by distance. */
export function pointAlong(route: Point[], t: number): Point {
  if (route.length === 0) return { x: 0, y: 0 };
  if (route.length === 1 || t <= 0) return route[0]!;
  if (t >= 1) return route[route.length - 1]!;
  const lengths: number[] = [];
  let total = 0;
  for (let i = 1; i < route.length; i += 1) { const d = Math.hypot(route[i]!.x - route[i - 1]!.x, route[i]!.y - route[i - 1]!.y); lengths.push(d); total += d; }
  if (total === 0) return route[route.length - 1]!;
  let want = t * total;
  for (let i = 0; i < lengths.length; i += 1) {
    if (want <= lengths[i]!) {
      const k = lengths[i]! === 0 ? 0 : want / lengths[i]!;
      return { x: route[i]!.x + (route[i + 1]!.x - route[i]!.x) * k, y: route[i]!.y + (route[i + 1]!.y - route[i]!.y) * k };
    }
    want -= lengths[i]!;
  }
  return route[route.length - 1]!;
}

/** How long a route should take to play out: a beat a square, bounded so a long walk is not a wait. */
export function travelMs(route: Point[], pitch: number): number {
  let total = 0;
  for (let i = 1; i < route.length; i += 1) total += Math.hypot(route[i]!.x - route[i - 1]!.x, route[i]!.y - route[i - 1]!.y);
  return Math.max(120, Math.min(1500, (total / Math.max(1, pitch)) * 120));
}
